import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFileProgress } from '../../../../features/tasks/hooks/useFileProgress';
import ProgressIndicator from '../../../../features/tasks/components/TaskRow/ProgressIndicator/ProgressIndicator';
import SaveButton from '../../../../ui/components/SaveButton/SaveButton';
import { formatFileCreatedDate, shouldShowFileCreatedDate } from '../../utils/fileStateHelpers';
import { svgDataUrlToPngDataUrl } from '../../utils/faviconUtils';
import './FileName.css';

const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.svg`;

function FileName({ fileName, isEditing, onFileNameClick, onFileNameBlur, onFileNameKeyDown, tasks, currentFileId, files, currentFile, hasUnsavedChanges = false, hasRestoredDraft = false, loggedOutWorkspaceSnapshot = null, editHighlightAnchorRef, onEditHighlightAnchorReady, shouldAnchorHighlightBar = false, minimal = false, value, onChange, placeholder, ariaLabel, showSaveButton = true }) {
  const elementRef = useRef(null);
  const wasEditingRef = useRef(false);
  const faviconSourceRef = useRef(null);
  const faviconUpdateIdRef = useRef(0);

  // Stable ref callback so React doesn't re-run it after parent state update (avoids update loop)
  const editHighlightAnchorRefCallback = useCallback(
    (el) => {
      if (!editHighlightAnchorRef) return;
      if (el) {
        if (editHighlightAnchorRef.current !== el) {
          editHighlightAnchorRef.current = el;
          onEditHighlightAnchorReady?.();
        }
      }
    },
    [editHighlightAnchorRef, onEditHighlightAnchorReady]
  );

  const { fileStats, virtualTask } = useFileProgress(tasks, currentFileId);

  // Helper functions for ProgressIndicator (not used for file-level, but required by component)
  const getSubtaskStatusColor = () => null;
  const getSubtaskStatusBorderColor = () => null;

  // Sync favicon to current file's progress indicator; convert SVG to PNG for broad browser support
  useEffect(() => {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const updateId = ++faviconUpdateIdRef.current;

    const applyPngFavicon = (pngDataUrl) => {
      if (faviconUpdateIdRef.current !== updateId) return;
      link.href = pngDataUrl;
      link.type = 'image/png';
    };

    if (!virtualTask || !fileStats || fileStats.total === 0) {
      fetch(DEFAULT_FAVICON)
        .then((r) => r.text())
        .then((svgText) => {
          const svgDataUrl = `data:image/svg+xml,${encodeURIComponent(svgText)}`;
          return svgDataUrlToPngDataUrl(svgDataUrl);
        })
        .then(applyPngFavicon)
        .catch(() => {
          if (faviconUpdateIdRef.current !== updateId) return;
          link.href = DEFAULT_FAVICON;
          link.type = 'image/svg+xml';
        });
      return;
    }
    const container = faviconSourceRef.current;
    const svgEl = container?.querySelector('.progress-circle-svg');
    if (!svgEl) return;
    const svgString = new XMLSerializer().serializeToString(svgEl);
    const svgDataUrl = `data:image/svg+xml,${encodeURIComponent(svgString)}`;
    svgDataUrlToPngDataUrl(svgDataUrl).then(applyPngFavicon);
  }, [virtualTask, fileStats]);

  // Update document title to selected file name
  useEffect(() => {
    document.title = fileName ? `${fileName} - picotodo` : 'picotodo';
  }, [fileName]);

  // Show creation date suffix when multiple files share the same name
  const showCreatedDate = !isEditing && shouldShowFileCreatedDate(files, fileName) && currentFile?.created_at;
  const createdDateSuffix = showCreatedDate ? formatFileCreatedDate(currentFile.created_at) : null;

  // Paste: insert plain text only to prevent XSS from pasted HTML
  const handleFileNamePaste = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    document.execCommand('insertText', false, pastedText);
  };

  useEffect(() => {
    if (!elementRef.current) return;
    const element = elementRef.current;

    if (isEditing && !wasEditingRef.current) {
      // Entering edit mode: set plain text
      element.textContent = fileName;
      wasEditingRef.current = true;
      element.focus();
      const range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (!isEditing && wasEditingRef.current) {
      // Exiting edit mode: restore display
      element.textContent = fileName;
      wasEditingRef.current = false;
    } else if (!isEditing && !wasEditingRef.current) {
      // Display mode: show file name
      if (element.textContent !== fileName) {
        element.textContent = fileName;
      }
    }
  }, [isEditing, fileName]);

  if (minimal && value !== undefined && onChange) {
    return (
      <input
        type="text"
        className="file-name file-name-minimal"
        value={value}
        onChange={onChange}
        placeholder={placeholder || 'My todolist'}
        aria-label={ariaLabel || 'List name'}
      />
    );
  }

  const anchorHighlightBar = editHighlightAnchorRef && (isEditing || shouldAnchorHighlightBar);
  return (
    <div
      className="file-name-container"
      ref={anchorHighlightBar ? editHighlightAnchorRefCallback : undefined}
    >
      {virtualTask && fileStats && fileStats.total > 0 ? (
        <>
          <ProgressIndicator
            task={virtualTask}
            tasks={tasks}
            subtaskStats={fileStats}
            isSequentialDisabled={false}
            isBlockingTaskHighlighted={false}
            isBlockingTaskNotBlocked={false}
            onBlockedIconMouseEnter={undefined}
            onBlockedIconMouseLeave={undefined}
            summaryLabel="File task status"
            getSubtaskStatusColor={getSubtaskStatusColor}
            getSubtaskStatusBorderColor={getSubtaskStatusBorderColor}
          />
          {/* Hidden clone used only to capture SVG for favicon */}
          <div ref={faviconSourceRef} aria-hidden="true" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', clipPath: 'inset(50%)' }}>
            <ProgressIndicator
              task={virtualTask}
              tasks={tasks}
              subtaskStats={fileStats}
              isSequentialDisabled={false}
              isBlockingTaskHighlighted={false}
              isBlockingTaskNotBlocked={false}
              onBlockedIconMouseEnter={undefined}
              onBlockedIconMouseLeave={undefined}
              summaryLabel="File task status"
              getSubtaskStatusColor={getSubtaskStatusColor}
              getSubtaskStatusBorderColor={getSubtaskStatusBorderColor}
            />
          </div>
        </>
      ) : null}
      <h1
        ref={elementRef}
        className={`file-name ${isEditing ? 'file-name-editing' : ''}`}
        contentEditable={isEditing}
        suppressContentEditableWarning={isEditing}
        onClick={!isEditing ? onFileNameClick : undefined}
        onBlur={(e) => isEditing && onFileNameBlur(e)}
        onKeyDown={(e) => isEditing && onFileNameKeyDown(e)}
        onPaste={handleFileNamePaste}
      />
      {createdDateSuffix && (
        <span className="file-name-date-suffix">- {createdDateSuffix}</span>
      )}
      {showSaveButton && (
        <SaveButton
          tasks={tasks}
          currentFileId={currentFileId}
          currentFileName={fileName}
          hasUnsavedChanges={hasUnsavedChanges}
          hasRestoredDraft={hasRestoredDraft}
          loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
        />
      )}
    </div>
  );
}

export default FileName;

